// What the server knows about a host that just ran the install command
// (installer ontwerp §1 step 5): the facts the agent reported, where the
// script was fetched from, and Approve / Reject. The admin approves only the
// enrollment they saw: Approve sends this card's `agent.enrolledAt`, and a
// 409 `enrollment_changed` makes the page refresh the card with the new facts.

import type { SandboxHost, SandboxHostAgent } from '@goblic/shared'
import { formatDateTime, formatRelative } from '../lib/format'
import { Facts, Notice } from './ui'

/** A host row's agent state as one badge: a StatusBadge status (for its colour) plus the label. */
export function agentBadge(
  host: SandboxHost,
  now: number = Date.now()
): { status: string; label: string } | null {
  const agent = host.agent
  if (!agent) return null
  switch (agent.state) {
    case 'waiting': {
      const expired = agent.tokenExpiresAt != null && Date.parse(agent.tokenExpiresAt) <= now
      return { status: 'unknown', label: expired ? 'Install token expired' : 'Waiting for install' }
    }
    case 'pending':
      return {
        status: 'pending',
        label: agent.connected ? 'Connected, awaiting approval' : 'Pending approval',
      }
    case 'connected':
      return host.health.status === 'healthy'
        ? { status: 'healthy', label: 'Healthy' }
        : { status: 'active', label: 'Connected' }
    case 'offline':
      return {
        status: 'degraded',
        label: agent.lastSeenAt ? `Offline since ${shortTime(agent.lastSeenAt)}` : 'Offline',
      }
  }
}

function shortTime(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? '–'
    : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

/** The "update pending / paused / failed" line of the Agent column, or null when current. */
export function updateIndicator(
  agent: SandboxHostAgent,
  autoUpdate: boolean | undefined
): { text: string; tone: 'info' | 'warning' | 'danger' } | null {
  const target = agent.update.targetVersion
  switch (agent.update.state) {
    case 'current':
      return null
    case 'update-available':
      // Paused: the installation switch is off, so only "Update agent now" moves it.
      return autoUpdate === false
        ? { text: `Update paused${target ? ` (${target})` : ''}`, tone: 'warning' }
        : { text: `Update pending${target ? ` (${target})` : ''}`, tone: 'info' }
    case 'requested':
      return { text: `Update requested${target ? ` (${target})` : ''}`, tone: 'info' }
    case 'updating':
      return { text: `Updating${target ? ` to ${target}` : ''}…`, tone: 'info' }
    case 'failed':
      return {
        text: `Update failed${agent.update.error ? `: ${agent.update.error}` : ''}`,
        tone: 'danger',
      }
  }
}

/** Reasons to look twice before approving. The admin judges the hostname themselves. */
export function approvalWarnings(agent: SandboxHostAgent): string[] {
  const warnings: string[] = []
  const { script } = agent
  if (script.lastFetchIp && agent.enrolledIp && script.lastFetchIp !== agent.enrolledIp)
    warnings.push(
      `The script was fetched from ${script.lastFetchIp} but the host enrolled from ${agent.enrolledIp}.`
    )
  if (script.fetchCount > 1)
    warnings.push(`The install script was fetched ${script.fetchCount} times, not once.`)
  return warnings
}

function memoryText(bytes: number): string {
  return `${(bytes / 1024 ** 3).toFixed(1)} GiB`
}

export function AgentApprovalCard({
  host,
  busy = false,
  notice,
  onApprove,
  onReject,
}: {
  host: SandboxHost
  busy?: boolean
  /** Shown above the buttons, e.g. "The host enrolled again; check the new details". */
  notice?: string | null
  onApprove: (enrolledAt: string) => void
  onReject: () => void
}) {
  const agent = host.agent
  if (!agent || agent.state !== 'pending' || !agent.enrolledAt) return null
  const enrolledAt = agent.enrolledAt
  const facts = agent.facts
  const warnings = approvalWarnings(agent)
  const { script } = agent
  return (
    <section
      aria-label={`Approve ${host.name}`}
      className="rounded-control border border-signal/40 bg-signal-soft/40 p-4"
    >
      <h3 className="text-base font-semibold text-ink">Approve this host?</h3>
      <p className="mt-0.5 mb-3 text-sm text-ink-2">
        Approve only if this is the machine you just installed.
      </p>
      <Facts
        columns={3}
        items={[
          { label: 'Enrolled from IP', value: agent.enrolledIp ?? '–' },
          { label: 'Enrolled', value: formatDateTime(agent.enrolledAt) },
          { label: 'Hostname', value: facts?.hostname ?? '–' },
          { label: 'OS', value: facts ? `${facts.os} · ${facts.arch}` : '–' },
          { label: 'CPU', value: facts ? `${facts.cpus} CPU${facts.cpus === 1 ? '' : 's'}` : '–' },
          { label: 'Memory', value: facts ? memoryText(facts.memoryBytes) : '–' },
          { label: 'Docker', value: facts?.dockerVersion || 'Not reachable' },
          { label: 'Agent', value: facts?.agentVersion ?? agent.version ?? '–' },
          {
            label: 'Script fetched',
            value:
              script.fetchCount === 0
                ? 'Never'
                : `${script.fetchCount}×${script.lastFetchedAt ? `, last ${formatRelative(script.lastFetchedAt)}` : ''}`,
          },
          { label: 'Last fetch IP', value: script.lastFetchIp ?? '–' },
          {
            label: 'First fetch',
            value: script.firstFetchedAt ? formatDateTime(script.firstFetchedAt) : '–',
          },
        ]}
      />
      {warnings.length > 0 ? (
        <div className="mt-3 space-y-2">
          {warnings.map((text) => (
            <Notice key={text} tone="signal">
              {text}
            </Notice>
          ))}
        </div>
      ) : null}
      {notice ? (
        <div className="mt-3">
          <Notice tone="signal">{notice}</Notice>
        </div>
      ) : null}
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" className="btn-secondary" disabled={busy} onClick={onReject}>
          Reject
        </button>
        <button type="button" className="btn-primary" disabled={busy} onClick={() => onApprove(enrolledAt)}>
          Approve host
        </button>
      </div>
    </section>
  )
}
